/**
 * Simple cache utility for Firebase data
 * Uses localStorage with expiration to reduce repeated reads
 */
class FirebaseCache {
  constructor() {
    this.cachePrefix = 'firebase_cache_';
    this.invalidationPrefix = 'firebase_invalidation_';
    this.lastRefreshDateKey = 'firebase_last_refresh_date';
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.maxCacheTTL = 60 * 60 * 1000; // 1 hour max TTL for aggressive caching
  }

  /**
   * Get cached data if it exists and hasn't expired
   * Also checks invalidation flags before returning cached data
   */
  get(key) {
    try {
      // Check if this cache key has been marked for invalidation
      if (this.isInvalidated(key)) {
        console.log(`🔄 Cache invalidated for key: ${key}`);
        this.clearKey(key);
        this.clearInvalidationFlag(key);
        return null;
      }

      const cached = localStorage.getItem(this.cachePrefix + key);
      if (!cached) return null;

      const { data, timestamp, ttl } = JSON.parse(cached);
      const now = Date.now();

      if (now - timestamp > ttl) {
        localStorage.removeItem(this.cachePrefix + key);
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Cache read error:', error);
      return null;
    }
  }

  /**
   * Set data in cache with optional TTL
   */
  set(key, data, ttl = this.defaultTTL) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl
      };
      localStorage.setItem(this.cachePrefix + key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Cache write error:', error);
    }
  }

  /**
   * Clear all cached data
   */
  clear() {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.cachePrefix))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Cache clear error:', error);
    }
  }

  /**
   * Clear only policy-related cache entries
   */
  clearPolicies(agentId = null) {
    try {
      const pattern = 'policies';
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.cachePrefix + pattern))
        .filter(key => !agentId || key.includes(agentId))
        .forEach(key => localStorage.removeItem(key));

      // Also clear specific policy keys
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.cachePrefix + 'policy_'))
        .filter(key => !agentId || key.includes(agentId))
        .forEach(key => localStorage.removeItem(key));

      console.log('✅ Policy cache cleared');
    } catch (error) {
      console.warn('Cache clear policies error:', error);
    }
  }

  /**
   * Clear only client-related cache entries
   */
  clearClients(agentId = null) {
    try {
      const pattern = 'clients';
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.cachePrefix + pattern))
        .filter(key => !agentId || key.includes(agentId))
        .forEach(key => localStorage.removeItem(key));

      // Also clear specific client keys
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.cachePrefix + 'client_'))
        .filter(key => !agentId || key.includes(agentId))
        .forEach(key => localStorage.removeItem(key));

      console.log('✅ Client cache cleared');
    } catch (error) {
      console.warn('Cache clear clients error:', error);
    }
  }

  /**
   * Clear specific policy by ID
   */
  clearPolicy(agentId, policyId) {
    try {
      const key = `policy_${agentId}_${policyId}`;
      this.clearKey(key);
      // Also clear the all-policies cache
      this.clearPolicies(agentId);
    } catch (error) {
      console.warn('Cache clear policy error:', error);
    }
  }

  /**
   * Clear specific client by ID
   */
  clearClient(agentId, clientId) {
    try {
      const key = `client_${agentId}_${clientId}`;
      this.clearKey(key);
      // Also clear the all-clients cache
      this.clearClients(agentId);
    } catch (error) {
      console.warn('Cache clear client error:', error);
    }
  }

  /**
   * Clear specific cache entry
   */
  clearKey(key) {
    try {
      localStorage.removeItem(this.cachePrefix + key);
    } catch (error) {
      console.warn('Cache clear key error:', error);
    }
  }

  /**
   * Generate cache key for client data
   */
  getClientCacheKey(agentId, pageSize, searchTerm, tabFilter, page) {
    return `clients_${agentId}_${pageSize}_${searchTerm}_${tabFilter}_${page}`;
  }

  /**
   * Generate cache key for policy data
   */
  getPolicyCacheKey(agentId, pageSize, page) {
    return `policies_${agentId}_${pageSize}_${page}`;
  }

  /**
   * Generate cache key for meeting data
   */
  getMeetingCacheKey(userId, pageSize, page) {
    return `meetings_${userId}_${pageSize}_${page}`;
  }

  /**
   * Mark cache entries for invalidation without immediately clearing them
   * This allows navigation to complete before cache is cleared on next read
   */
  markForInvalidation(pattern) {
    try {
      const keys = Object.keys(localStorage)
        .filter(key => key.startsWith(this.cachePrefix))
        .filter(key => {
          const cacheKey = key.replace(this.cachePrefix, '');
          return cacheKey.includes(pattern);
        });

      keys.forEach(key => {
        const cacheKey = key.replace(this.cachePrefix, '');
        localStorage.setItem(this.invalidationPrefix + cacheKey, Date.now().toString());
      });

      console.log(`🔄 Marked ${keys.length} cache entries for invalidation (pattern: ${pattern})`);
    } catch (error) {
      console.warn('Mark for invalidation error:', error);
    }
  }

  /**
   * Check if a cache key has been marked for invalidation
   */
  isInvalidated(key) {
    try {
      return localStorage.getItem(this.invalidationPrefix + key) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear invalidation flag for a key
   */
  clearInvalidationFlag(key) {
    try {
      localStorage.removeItem(this.invalidationPrefix + key);
    } catch (error) {
      console.warn('Clear invalidation flag error:', error);
    }
  }

  /**
   * Clear all invalidation flags
   */
  clearAllInvalidationFlags() {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(this.invalidationPrefix))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Clear all invalidation flags error:', error);
    }
  }

  /**
   * Invalidate cache for specific screens after write operations
   * Based on the write operation rules:
   * - Renewals write: Invalidate all screens
   * - ClientList write: Invalidate ClientList, PolicyList, Renewals
   * - PolicyDetails write: Invalidate PolicyDetails and related screens
   * - ClientDetails write: Invalidate ClientList, ClientDetails
   */
  invalidateAfterWrite(operation, agentId) {
    console.log(`🔄 Invalidating cache after ${operation}`);

    switch (operation) {
      case 'renewals_write': // Upload Policy or Enter Policy from Renewals
        // Invalidate ALL screens
        this.markForInvalidation('policies');
        this.markForInvalidation('clients');
        console.log('✅ Invalidated: All screens (Renewals write)');
        break;

      case 'clientlist_write': // New Lead from ClientList
        // Invalidate ClientList, PolicyList, Renewals
        this.markForInvalidation('clients');
        this.markForInvalidation('policies');
        console.log('✅ Invalidated: ClientList, PolicyList, Renewals');
        break;

      case 'policydetails_write': // Edit or Renew from PolicyDetails
        // Invalidate policy-related screens
        this.markForInvalidation('policies');
        this.markForInvalidation(`policy_${agentId}`);
        console.log('✅ Invalidated: PolicyDetails and related screens');
        break;

      case 'clientdetails_write': // Edit Profile or Delete from ClientDetails
        // Invalidate ClientList, ClientDetails, and Policy caches since policy documents are also updated
        this.markForInvalidation('clients');
        this.markForInvalidation(`client_${agentId}`);
        this.markForInvalidation('policies');
        this.markForInvalidation(`policy_${agentId}`);
        console.log('✅ Invalidated: ClientList, ClientDetails, PolicyList, PolicyDetails');
        break;

      default:
        console.warn('Unknown write operation:', operation);
    }
  }

  /**
   * Check if cache should be refreshed daily
   * Returns true if it's a new calendar day since last refresh
   */
  shouldRefreshDaily() {
    try {
      const lastRefreshDate = localStorage.getItem(this.lastRefreshDateKey);
      const today = new Date().toDateString();

      if (!lastRefreshDate || lastRefreshDate !== today) {
        console.log('🌅 New day detected - cache refresh needed');
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Daily refresh check error:', error);
      return false;
    }
  }

  /**
   * Mark that cache has been refreshed today
   */
  markDailyRefresh() {
    try {
      const today = new Date().toDateString();
      localStorage.setItem(this.lastRefreshDateKey, today);
      console.log('✅ Daily refresh marked for:', today);
    } catch (error) {
      console.warn('Mark daily refresh error:', error);
    }
  }

  /**
   * Force refresh all cached data (for daily refresh)
   * This marks all cache entries for invalidation
   */
  forceRefreshAll() {
    try {
      console.log('🔄 Force refreshing all cached data');
      this.markForInvalidation('policies');
      this.markForInvalidation('clients');
      this.markDailyRefresh();
      console.log('✅ All cache marked for refresh');
    } catch (error) {
      console.warn('Force refresh error:', error);
    }
  }

  /**
   * Get statistics about cache usage
   */
  getStats() {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(this.cachePrefix));
      const invalidationKeys = Object.keys(localStorage).filter(key => key.startsWith(this.invalidationPrefix));
      const stats = {
        totalEntries: keys.length,
        policyEntries: keys.filter(k => k.includes('policy')).length,
        clientEntries: keys.filter(k => k.includes('client')).length,
        dropdownEntries: keys.filter(k => k.includes('dropdown')).length,
        invalidationFlags: invalidationKeys.length,
        totalSize: keys.reduce((acc, key) => acc + (localStorage.getItem(key)?.length || 0), 0)
      };
      return stats;
    } catch (error) {
      console.warn('Cache stats error:', error);
      return null;
    }
  }
}

// Export singleton instance
export const firebaseCache = new FirebaseCache();
